import React, { useRef, useState } from 'react';
import { Typography } from '@mui/material';
import * as Moment from 'moment';
import { extendMoment } from 'moment-range';
import Parser from 'html-react-parser';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import styled from 'styled-components';
import * as theme from '../../theme';

const moment = extendMoment(Moment);

const days = [
    { name: 'Monday', short: 'Mo' },
    { name: 'Tuesday', short: 'Tu' },
    { name: 'Wednesday', short: 'We' },
    { name: 'Thursday', short: 'Th' },
    { name: 'Friday', short: 'Fr' },
];

const heightPerHour = 60;
const stepsPerHour = 2;
const defaultStartTime = moment('08:30:00', 'HH:mm:ss');
const defaultEndTime = moment('17:30:00', 'HH:mm:ss');
const borderColor = '#000';

const Timetable = (props) => {
    const {
        index,
        timetable: { timetable },
        type,
        ID,
        selectedItem,
        onSelectItem,
        onSelectTimeslot,
        changedTimeslot,
        isTimeTravel,
        isReadOnly,
        emptySlots,
        label,
    } = props;

    const [view, setView] = useState('week');
    const [activeDay, setActiveDay] = useState(0);

    const slider1Ref = useRef(null);
    const slider2Ref = useRef(null);

    const createTimetable = () => {
        const {
            config: { minStart, maxEnd },
        } = timetable;

        // Convert string values to dates
        const configStartTime = moment(minStart, 'HH:mm:ss');

        // We have to round up the config end time to full 30 minutes
        const remainder = 30 - (moment(maxEnd, 'HH:mm:ss').minute() % 30);
        const configEndTime = remainder < 30 ? moment(maxEnd, 'HH:mm:ss').add(remainder, 'minutes') : moment(maxEnd, 'HH:mm:ss');

        // Check if config values are out of default range
        const startTime = configStartTime < defaultStartTime ? configStartTime : defaultStartTime;
        const endTime = configEndTime > defaultEndTime ? configEndTime : defaultEndTime;

        // Get times for sidebar
        const times = [];
        for (let i = startTime.clone(); i.isSameOrBefore(endTime.clone()); i.add(1 / stepsPerHour, 'hours')) {
            times.push(i.format('HH:mm:ss'));
        }

        // Generate active week + date values for pagination
        const termStartDate = moment();
        const termStartWeek = termStartDate.week();

        // we need to calculate the weeks from the beginning, because the end date can be in a new year
        // and therefore it would start with 1 again
        const termEndDate = moment();
        const termEndWeek = termEndDate.diff(termStartDate, 'week') + termStartWeek;

        const currentWeek = termStartWeek + 1;

        let week = termStartWeek;
        if (currentWeek >= termStartWeek && currentWeek <= termEndWeek + 1) {
            week = currentWeek - 1;
        }

        const settings = {
            speed: 500,
            slidesToShow: view === 'day' ? 1 : 5,
            slidesToScroll: 1,
            infinite: false,
            beforeChange: (oldIndex, newIndex) => {
                setActiveDay(newIndex);

                if (slider2Ref && slider2Ref.current) {
                    slider2Ref.current.slickGoTo(newIndex);
                }
            },
        };

        const renderConflictType = (conflict) => {
            let value = '';

            if (conflict.isInstructorConflict) value += '[IC]';

            if (conflict.isSetConflict) value += ' [SC]';

            if (conflict.isRoomConflict) value += ' [RC]';

            return value;
        };

        const renderTime = (p) => {
            const conflictStr = renderConflictType(p.conflict);

            return <p style={{ marginTop: '30px' }} children={`${moment(p.timeIn, 'HH:mm:ss').format('hh:mm')} to ${moment(p.timeOut, 'HH:mm:ss').format('hh:mm')} ${conflictStr}`} />;
        };

        const renderCourse = (p) => {
            let classType = '';

            if (Array.isArray(p.sections) && p.sections.length > 0 && p.sections[0].classType) {
                classType = p.sections[0].classType;
            }

            return <p children={`${p.course.subject} ${p.course.courseNumber} ${classType}`} />;
        };

        const renderSets = (p) => {
            //list of sets in not rendered for set timetable(s)
            // if (type == 0) return null;

            let displayCode = '';

            //FIXME: This is hack as the backend returns the wrong set code for conflicts
            if (Array.isArray(p.sets) && p.sets.length === 1) displayCode = p.sets[0].setCode;

            return (
                <p>
                    {p.sets.map((q) => {
                        return <span key={q.setID} children={displayCode || q.displayCode + ' '} />;
                    })}
                </p>
            );
        };

        const renderInstructors = (p) => {
            return (
                <p>
                    {p.instructors.map((q, index) => {
                        if (q !== null) {
                            return <span key={q.instructorID} children={q.lastName + ' ' + q.firstName + '  '} />;
                        } else {
                            return null;
                        }
                    })}
                </p>
            );
        };

        const getContent = (data) => {
            const content = [data];

            if (data.isEmptySlot) {
                return 'Open Slot';
            }

            if (data.isOfficeHour && data.isOfficeHourAppointment) {
                return (
                    <div>
                        {renderTime(data)}
                        <p children={'Appointment Based'} />
                    </div>
                );
            }

            if (data.isOfficeHour && !data.isOfficeHourAppointment) {
                return (
                    <div>
                        {renderTime(data)}
                        <p children={Parser(data.officeHourNote || '')} />
                    </div>
                );
            }

            if (data.isEvent) {
                return (
                    <div>
                        {/* TimeIn and TimeOut are Strings */}
                        {data.timeIn != minStart || (data.timeOut != maxEnd && renderTime(data))}
                        <p children={Parser(data.note || '')} />
                        {/* TODO: Add to API */}
                        {data.room && <section children={`${data.buildingCode} ${data.room.roomNumber}`} />}
                    </div>
                );
            }

            return <div>{content.map((s, index) => renderTimetableCell(s, index))}</div>;
        };

        const renderTimetableCell = (classData, index) => {
            return (
                <>
                    {renderTime(classData)}
                    {classData.course && renderCourse(classData)}

                    {classData.room && <p children={`${classData.buildingCode} ${classData.room.roomNumber}`} />}

                    {classData.instructors && renderInstructors(classData)}

                    <p> {classData.sections && classData.sections.map((section) => '(' + section.crn.toString() + ' - ' + section.enrolled + '/' + section.roomCap + ') ')} </p>

                    {classData.sets && renderSets(classData)}
                </>
            );
        };

        const getSectionConflicts = (section) => {
            const conflicts = timetable.conflicts;
            let overlappingConflicts = [];

            if (Array.isArray(conflicts) && conflicts.length > 0) {
                overlappingConflicts = conflicts.filter((c) => c.timeIn < section.timeOut && section.timeIn < c.timeOut && c.dayOfWeek === section.dayOfWeek);
            }
            return overlappingConflicts;
        };

        const getHeightOfSelectedItem = () => {
            const y = moment(selectedItem.timeIn, 'HH:mm:ss').diff(startTime, 'minutes') / 60;

            // We have to round the end time to 30-minutes
            const remainder = 30 - (moment(selectedItem.timeOut, 'HH:mm:ss').minute() % 30);

            // We have to make sure that we don't round even numbers up as well
            const roundedTimeOut = remainder < 30 ? moment(selectedItem.timeOut, 'HH:mm:ss').add(remainder, 'minutes').format('HH:mm:ss') : moment(selectedItem.timeOut, 'HH:mm:ss');

            const height = moment(roundedTimeOut, 'HH:mm:ss').diff(startTime, 'minutes') / 60 - y;

            return height;
        };

        //New slot can only be selected for the SET -> SET change
        const renderClickableElements = (weekDayIndex) => {
            if (index === 'to' && !!selectedItem && !isTimeTravel && type === 0) {
                const height = getHeightOfSelectedItem();

                return times.map((timeIn, i) => {
                    let top = moment(timeIn, 'HH:mm:ss').diff(moment(startTime, 'HH:mm:ss'), 'minutes') / 60;

                    // There is a calculation error for times after 1pm.
                    if (top < 0) {
                        top = top + 12;
                    }

                    return (
                        <Timeslot
                            key={timeIn}
                            onClick={() => onSelectTimeslot({ weekDayIndex, timeIn, instructorId: '', roomId: '' })}
                            style={{
                                zIndex: 10 + i,
                                top: top * heightPerHour,
                                height: height * heightPerHour - 1,
                            }}
                        />
                    );
                });
            }
        };

        const handleSelectItem = (item, weekDayIndex) => {
            if (isTimeTravel || !item.isBattleshipChangeable) {
                return;
            }

            if (index === 'from') {
                onSelectItem(selectedItem?.id === item.id ? null : item);
            }
        };

        return (
            <Container>
                {props.label && (
                    <Label>
                        <LabelText>{props.label}</LabelText>
                    </Label>
                )}
                <DayPagination>
                    {days.map((o, i) => {
                        //TODD: Account for term ending in the year after
                        const year = moment(termStartDate).year();

                        return (
                            <Day
                                key={o.short}
                                onClick={() => {
                                    if (slider1Ref && slider1Ref.current) {
                                        slider1Ref.current.slickGoTo(i);
                                    }
                                    setActiveDay(i);
                                }}
                                active={i === activeDay}
                                view={view}
                            >
                                {o.name}
                            </Day>
                        );
                    })}
                </DayPagination>

                <TimetableContainer>
                    <Sidebar>
                        {times.map((time) => {
                            return (
                                <TimeUnit key={time} showLine={!time.includes(':30')}>
                                    <span children={time.substring(0, time.length - 3)} />
                                </TimeUnit>
                            );
                        })}
                    </Sidebar>

                    <TimeTable hours={times.length / stepsPerHour}>
                        <Slider ref={slider1Ref} {...settings}>
                            {days.map((o, weekDayIndex) => {
                                let dailyTimetable = timetable.timetables ? timetable.timetables.filter((p) => p.dayOfWeek === o.name) : [];
                                let dailyEmptySlots = emptySlots ? emptySlots.filter((s) => s.dayOfWeek === o.name) : [];

                                if (dailyEmptySlots.length) dailyTimetable = [...dailyEmptySlots, ...dailyTimetable];

                                return (
                                    <div key={weekDayIndex}>
                                        {!isReadOnly && renderClickableElements(weekDayIndex)}

                                        {dailyTimetable.map((p) => {
                                            const y = moment(p.timeIn, 'HH:mm:ss').diff(startTime, 'minutes') / 60;

                                            // We have to round the end time to 30-minutes
                                            const remainder = 30 - (moment(p.timeOut, 'HH:mm:ss').minute() % 30);

                                            // We have to make sure that we don't round even numbers up as well
                                            const roundedTimeOut = remainder < 30 ? moment(p.timeOut, 'HH:mm:ss').add(remainder, 'minutes').format('HH:mm:ss') : moment(p.timeOut, 'HH:mm:ss');
                                            const height = moment(roundedTimeOut, 'HH:mm:ss').diff(startTime, 'minutes') / 60 - y;

                                            const content = getContent(p);
                                            let backgroundColor = '#EEEEEE';
                                            let textColor = theme.colors.black;
                                            let border = '';
                                            let onHover = { backgroundColor: '#EEF7FF' };

                                            if (p.id && !p.isBattleshipChangeable) {
                                                backgroundColor = '#E8E8E8';
                                            } else if (p.isOfficeHour && p.isOfficeHourAppointment) {
                                                backgroundColor = '#E3E2FF';
                                            } else if (p.isOfficeHour && !p.isOfficeHourAppointment) {
                                                backgroundColor = '#FFF2AF';
                                            } else if (p.id === 'break' || p.isEvent) {
                                                backgroundColor = '#E8E8E8';
                                            } else if (p?.conflict?.anyConflict) {
                                                backgroundColor = theme.colors.yellow.main;
                                                textColor = theme.colors.red.main;
                                            } else if (p.isEmptySlot) {
                                                backgroundColor = theme.colors.blue.main;
                                                textColor = '#fff';
                                                onHover = { backgroundColor: '#60B9FF' };
                                            }

                                            if (index === 'from' && selectedItem?.id === p.id) {
                                                backgroundColor = '#EEF7FF';
                                                border = `2px solid ${theme.colors.blue.main}`;
                                            }

                                            if (index === 'to') {
                                                if (p.id && !isReadOnly && changedTimeslot?.functionId === p.id) {
                                                    backgroundColor = '#BDFFBC';
                                                }
                                            }

                                            const style = {
                                                cursor: 'pointer',
                                            };

                                            if (isReadOnly) style.cursor = 'auto';

                                            return (
                                                <Content
                                                    key={p.id}
                                                    y={y}
                                                    height={height}
                                                    view={view}
                                                    backgroundColor={backgroundColor}
                                                    border={border}
                                                    textColor={textColor}
                                                    onHover={onHover}
                                                    style={style}
                                                    children={content}
                                                    onClick={() => handleSelectItem(p, weekDayIndex)}
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </Slider>
                    </TimeTable>
                </TimetableContainer>

                {timetable.conflicts.length > 0 && (
                    <ConflictTimetableContainer>
                        <TimetableContainer>
                            <Sidebar />
                            <ConflictHeader>
                                <Typography children="Conflicts" />
                            </ConflictHeader>
                        </TimetableContainer>

                        <TimetableContainer>
                            <Sidebar />
                            <ConflictTimeTable hours={times.length / stepsPerHour}>
                                <Slider ref={slider2Ref} {...settings} draggable={false}>
                                    {days.map((o, weekDayIndex) => {
                                        const dailyTimetable = timetable.conflicts ? timetable.conflicts.filter((p) => p.dayOfWeek === o.name) : [];
                                        return (
                                            <div key={o.name}>
                                                {dailyTimetable.map((p) => {
                                                    const classContent = getContent(p);

                                                    let backgroundColor = theme.colors.yellow.main;
                                                    let textColor = theme.colors.red.main;

                                                    if (index === 'from' && selectedItem?.id === p.id) {
                                                        backgroundColor = '#209DFF';
                                                    }

                                                    if (index === 'to') {
                                                        if (p.id && !isReadOnly && changedTimeslot?.functionId === p.id) {
                                                            backgroundColor = '#BDFFBC';
                                                        }
                                                    }

                                                    return (
                                                        <Conflict
                                                            key={p.id}
                                                            view={view}
                                                            children={classContent}
                                                            backgroundColor={backgroundColor}
                                                            textColor={textColor}
                                                            onClick={() => handleSelectItem(p, weekDayIndex)}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </Slider>
                            </ConflictTimeTable>
                        </TimetableContainer>
                    </ConflictTimetableContainer>
                )}
            </Container>
        );
    };

    return <>{timetable && createTimetable()}</>;
};

const Container = styled.div`
    overflow: hidden;
    background-color: #fff;
    width: 100%;
    transition: none;
`;

const Label = styled.div`
    text-align: center;
    margin-bottom: 5px;
`;

const LabelText = styled.p`
    font-size: 18px;
    font-weight: bold;
`;

const DayPagination = styled.div`
    display: flex;
    margin-left: 38px;
    background: ${theme.colors.blue.main};
    color: #fff;
    border-left: 1px solid ${borderColor};
    border-right: 1px solid ${borderColor};
    border-top: 1px solid ${borderColor};

    @media (min-width: 640px) {
        margin-left: 50px;
    }
`;

const Day = styled('div')`
    position: relative;
    z-index: 1;
    width: 20%;
    height: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    cursor: pointer;
    font-size: 10px;
    ${(props) => props.active && props.view === 'day' && 'color: #fff;'};

    &:before {
        content: '';
        position: absolute;
        z-index: -1;
        left: 50%;
        top: 3px;
        height: 44px;
        width: 44px;
        transform: translateX(-50%);
        border-radius: 50%;
        opacity: ${(props) => (props.active && props.view === 'day' ? 1 : 0)};
    }
`;

const TimetableContainer = styled.div`
    display: flex;
`;

const TimeTable = styled('div')`
    width: calc(100% - 38px);

    @media (min-width: 640px) {
        width: calc(100% - 50px);
    }

    .slick-list {
        border-right: 1px solid ${borderColor};
    }

    .slick-slider {
        width: 100%;
        background: #fff;
        border-top: 1px solid ${borderColor};
    }

    .slick-list {
        z-index: 2;
    }

    .slick-slide {
        position: relative;
        border-left: 1px solid ${borderColor};
        height: ${(props) => props.hours * heightPerHour - heightPerHour / 2}px;

        > div > div {
            display: block !important;
        }
    }
`;

const Sidebar = styled.div`
    width: 38px;

    @media (min-width: 640px) {
        width: 50px;
    }
`;

const TimeUnit = styled('div')`
    position: relative;
    display: flex;
    justify-content: center;
    height: ${heightPerHour / stepsPerHour}px;

    span {
        position: absolute;
        z-index: 0;
        right: 5px;
        top: -6px;
        font-size: 10px;
    }

    &:before {
        content: '';
        position: absolute;
        z-index: 1;
        left: 100%;
        top: 0;
        width: 100vw;
        height: 1px;
        background: ${borderColor};
        opacity: ${(props) => (props.showLine ? 0.3 : 1)};
    }
`;

const Content = styled.div`
    overflow: auto;
    position: absolute;
    left: 0;
    top: ${(props) => props.y * heightPerHour}px;
    width: 100%;
    height: ${(props) => `calc(${props.height * heightPerHour}px - 1px)`};
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    text-align: center;
    background: ${(props) => props.backgroundColor};
    color: ${(props) => props.textColor};
    border: ${(props) => props.border};

    @media (max-width: 640px) {
        padding: 2px;
    }

    > div {
        max-width: 100%;
    }

    p {
        margin-top: 0;
        margin-bottom: 0;
        font-size: 10px;
        line-height: 11px;
        color: ${(props) => props.textColor};

        @media (max-width: 640px) {
            ${(props) =>
                props.view === 'week' &&
                `
                font-size: 10px;
                line-height: 12px;
            `}
        }

        ${(props) =>
            props.view === 'day' &&
            `
            font-size: 14px;
            line-height: 16px;
      `}
    }
    &:hover {
        ${(props) => props.onHover && props.onHover}
    }
`;

const ConflictTimetableContainer = styled.div`
    margin-top: -${heightPerHour / 2}px;
    margin-bottom: ${heightPerHour / 2}px;
`;

const ConflictTimeTable = styled('div')`
    width: calc(100% - 38px);
    border-right: 1px solid ${borderColor};
    border-left: 1px solid ${borderColor};

    @media (min-width: 640px) {
        width: calc(100% - 50px);
    }

    .slick-slider {
        width: 100%;
        background: #fff;
        border-top: 1px solid ${borderColor};
        border-bottom: 1px solid ${borderColor};
    }

    .slick-list {
        z-index: 2;
    }

    .slick-track {
        > div:last-child {
        }
    }

    .slick-slide {
        position: relative;

        > div > div {
            outline: none;
            display: block !important;
        }
    }
`;

const ConflictHeader = styled.div`
    flex: 1;
    height: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #1687e3;
    color: #fff;
    border-left: 1px solid ${borderColor};
    border-right: 1px solid ${borderColor};

    p {
        font-size: 10px;
    }
`;

const Conflict = styled('div')`
    width: 100%;
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    text-align: center;
    overflow: hidden;
    border: 1px solid #fff;
    background: ${(props) => props.backgroundColor};
    cursor: pointer;

    @media (max-width: 640px) {
        padding: 2px;
    }

    > div {
        max-width: 100%;
    }

    p {
        margin-top: 0;
        margin-bottom: 0;
        font-size: 10px;
        line-height: 12px;
        color: ${(props) => props.textColor};

        @media (max-width: 640px) {
            ${(props) =>
                props.view === 'week' &&
                `
      font-size: 10px;
      line-height: 12px;
      `}
        }

        ${(props) =>
            props.view === 'day' &&
            `
        font-size: 14px;
        line-height: 16px;
      `}
    }
`;

const Timeslot = styled.div`
    position: absolute;
    left: 0;
    width: 100%;
    background: transparent;

    &:hover {
        background: #bdffbc;
        opacity: 0.5;
    }
`;

export default Timetable;
